import { date, index, jsonb, pgTable, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/**
 * Canonical application lifecycle — the single source of truth for an application's status.
 * GROUNDED in the Tracker board columns already shipped in the product
 * (apps/web/src/pages/Tracker.tsx: "Wishlist → Applied → Screen → Onsite → Offer → Closed").
 * Enforced by an additive DB CHECK on `applications.status` (P0, CHANGE-CONTROL R100).
 * NO INVENTION — these are the states the product already uses, not a guessed lifecycle.
 * (The separate `cover_letter_archives.outcome` lifecycle is intentionally NOT reconciled here.)
 */
export const APPLICATION_STATUSES = ['wishlist', 'applied', 'screen', 'onsite', 'offer', 'closed'] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

/**
 * Application lifecycle EVENTS — the event-sourced audit of an application's journey.
 * One row per transition (P1): 'created' on first track, 'status_changed' on each move
 * (metadata carries { from, to }). A small additive union so the event vocabulary can't drift.
 */
export const APPLICATION_EVENT_TYPES = ['created', 'status_changed'] as const;
export type ApplicationEventType = (typeof APPLICATION_EVENT_TYPES)[number];

/**
 * Outcome RESULT — what actually happened, distinct from `status` (a board stage). A
 * closed application can still be missing this (null = undetermined, never guessed).
 * Sibling vocabulary to `cover_letter_archives.outcome` MINUS 'pending' — `status`
 * already carries every in-flight state, so this union only names TERMINAL results.
 * NO INVENTION: never auto-set by the system — the user fills it in, e.g. from a
 * "mark closed?" offer (W1-7, additive, 2026-07-02, docs/briefs/launch-week-briefs.md §A).
 */
export const APPLICATION_OUTCOME_RESULTS = [
  'no_callback',
  'rejected',
  'withdrawn',
  'offer_accepted',
  'offer_declined',
] as const;
export type ApplicationOutcomeResult = (typeof APPLICATION_OUTCOME_RESULTS)[number];

export const applications = pgTable(
  'applications',
  {
    id: id(),
    // MIGRATION DEBT (flagged P0, additive-only — do NOT migrate here): user_id is text, not the
    // users.id uuid (CLAUDE.md multi-user-per-UUID law). Scope by this text user_id for now; a
    // uuid migration is a separate additive follow-up.
    userId: text('user_id').notNull(),
    jobId: text('job_id').notNull(),
    resumeId: text('resume_id'),
    coverLetterId: text('cover_letter_id'),
    status: varchar('status', { length: 24 }).$type<ApplicationStatus>().default('wishlist').notNull(),
    appliedAt: date('applied_at'),
    closedAt: date('closed_at'),
    closureReason: varchar('closure_reason', { length: 64 }),
    // Additive (W1-7, 2026-07-02): the user's own account of what happened — never
    // system-inferred. NULL = not yet told us (honest absence, not a fabricated result).
    outcomeResult: varchar('outcome_result', { length: 24 }).$type<ApplicationOutcomeResult>(),
    // Free-text "what happened" the user can attach alongside (or instead of) the enum —
    // e.g. "recruiter said the role was paused." Independently nullable; either field
    // may be set without the other.
    outcomeFeedback: text('outcome_feedback'),
    notes: text('notes'),
    metadata: jsonb('metadata'),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('applications_user_idx').on(t.userId),
    statusIdx: index('applications_status_idx').on(t.status),
  }),
);

export const applicationEvents = pgTable(
  'application_events',
  {
    id: id(),
    applicationId: text('application_id').notNull(),
    eventType: varchar('event_type', { length: 32 }).$type<ApplicationEventType>().notNull(),
    occurredAt: date('occurred_at').notNull(),
    summary: text('summary'),
    metadata: jsonb('metadata'),
    ...timestamps,
  },
  (t) => ({
    applicationIdx: index('app_events_application_idx').on(t.applicationId),
  }),
);

export const notes = pgTable('notes', {
  id: id(),
  userId: text('user_id').notNull(),
  scope: varchar('scope', { length: 24 }).notNull(),
  scopeId: text('scope_id').notNull(),
  body: text('body'),
  voiceR2Key: varchar('voice_r2_key', { length: 256 }),
  ...timestamps,
});
